import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { projectsApi } from '@/lib/api';

interface ProjectInfo {
  id: number;
  name: string;
  code: string;
  status: string;
}

interface ProjectState {
  currentProjectId: number | null;
  currentProject: ProjectInfo | null;
  projects: ProjectInfo[];
  isLoading: boolean;
  _version: number; // 프로젝트 변경 감지용

  setProject: (projectId: number) => void;
  fetchProjects: () => Promise<void>;
  initProject: () => Promise<void>;
  clearProject: () => void;
}

export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => ({
      currentProjectId: null,
      currentProject: null,
      projects: [],
      isLoading: false,
      _version: 0,

      fetchProjects: async () => {
        try {
          const projects = await projectsApi.getAll();
          set({ projects });
        } catch {
          // 로그인 전이면 무시
        }
      },

      setProject: (projectId: number) => {
        const { projects, currentProjectId } = get();
        if (projectId === currentProjectId) return; // 같은 프로젝트면 무시

        const project = projects.find(p => p.id === projectId) || null;
        set({
          currentProjectId: projectId,
          currentProject: project,
          _version: get()._version + 1,
        });

        // 프로젝트 전환 시 전체 페이지 리로드 (모든 데이터 갱신)
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      },

      initProject: async () => {
        const { currentProjectId, fetchProjects } = get();

        set({ isLoading: true });
        await fetchProjects();
        const { projects } = get();

        if (projects.length === 0) {
          set({ isLoading: false });
          return;
        }

        // 이미 선택된 프로젝트가 유효한지 확인
        if (currentProjectId) {
          const exists = projects.find(p => p.id === currentProjectId);
          if (exists) {
            set({ currentProject: exists, isLoading: false });
            return;
          }
        }

        // 첫 active 프로젝트 자동 선택
        const activeProject = projects.find(p => p.status === 'active') || projects[0];
        set({
          currentProjectId: activeProject.id,
          currentProject: activeProject,
          isLoading: false,
          _version: get()._version + 1,
        });
      },

      clearProject: () => {
        set({ currentProjectId: null, currentProject: null, projects: [] });
      },
    }),
    {
      name: 'omnimind_project',
      partialize: (state) => ({
        currentProjectId: state.currentProjectId,
        currentProject: state.currentProject,
      }),
    }
  )
);
